import React, { useState, useEffect, useMemo } from "react";
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import API_BASE_URL from "../config";
import SEO from "../components/SEO";
import "./Home.css";

const MONTHS_IN_YEAR = 12;

const BUNDLE_DEFINITIONS = [
  {
    id: "bundle-pos-basic",
    name: "POS Billing Basic",
    description: "POS bundle with plans 5 and 7.",
    planIds: [5, 7],
    percentage: 83.47,
  },
  {
    id: "bundle-pos-premium",
    name: "POS Billing Premium",
    description: "POS premium bundle with plans 5, 6, 7 and 8.",
    planIds: [5, 6, 7, 8],
    percentage: 83.47,
  },
  {
    id: "bundle-ota-hotels",
    name: "OTA's Software for Hotels and Resorts",
    description: "Non-POS hotel bundle with plans 1 and 2.",
    planIds: [1, 2],
    percentage: 42.97,
  },
  {
    id: "bundle-ota-restaurant-qr",
    name: "OTA's and Digital Qr menu for Your Restaurant and Café Needs",
    description: "Non-POS restaurant bundle with plans 3 and 4.",
    planIds: [3, 4],
    percentage: 42.97,
  },
];

const parsePrice = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^\d.]/g, "");
    return Number(cleaned) || 0;
  }
  return 0;
};

const toPlanId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrency = (amount) => {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const roundedUpToNearestThousand = safeAmount > 0
    ? Math.ceil(safeAmount / 1000) * 1000
    : 0;
  return roundedUpToNearestThousand.toLocaleString("en-IN");
};

const createBundles = (planList) => {
  if (!planList || planList.length === 0) return [];

  const plansById = new Map(planList.map((plan) => [toPlanId(plan.id), plan]));

  return BUNDLE_DEFINITIONS.map((definition) => {
    const bundlePlans = definition.planIds
      .map((planId) => plansById.get(planId))
      .filter(Boolean);

    const monthlyTotal = bundlePlans.reduce((sum, plan) => sum + parsePrice(plan.basePrice), 0);
    const yearlyTotal = monthlyTotal * MONTHS_IN_YEAR;
    const percentagePrice = (yearlyTotal * definition.percentage) / 100;

    return {
      id: definition.id,
      name: definition.name,
      description: definition.description,
      percentage: definition.percentage,
      plans: bundlePlans,
      monthlyTotal,
      yearlyTotal,
      percentagePrice,
    };
  }).filter((bundle) => bundle.plans.length > 0);
};

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [expandedBundleId, setExpandedBundleId] = useState(null);

  const bundledPlans = useMemo(() => createBundles(plans), [plans]);

  useEffect(() => {
    console.log('[Pricing] Component mounted, fetching plans...');
    console.log('[Pricing] API_BASE_URL:', API_BASE_URL);
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    console.log('[Pricing] Fetching plans from:', `${API_BASE_URL}/api/pricing/plans`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/pricing/plans`);
      console.log('[Pricing] Response status:', response.status);
      if (!response.ok) {
        console.error('[Pricing] Failed to fetch plans:', response.statusText);
        throw new Error("Failed to fetch pricing plans");
      }
      const data = await response.json();
      console.log('[Pricing] Fetched plans:', data);
      console.log('[Pricing] Number of plans:', data.length);
      data.forEach((plan, index) => {
        console.log(`[Pricing] Plan ${index + 1}:`, {
          id: plan.id,
          name: plan.name,
          basePrice: plan.basePrice,
          modules: plan.modules?.length || 0,
          durations: plan.durations?.length || 0
        });
      });
      setPlans(data);
    } catch (err) {
      console.error('[Pricing] Error fetching plans:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    console.log('[Pricing] Submit lead clicked for plan:', selectedPlan?.name);
    if (!phone || phone.replace(/\D/g, '').length < 7) {
      console.warn('[Pricing] Invalid phone number:', phone);
      setSubmitMessage('Please enter a valid phone number');
      return;
    }
    setSubmitting(true);
    setSubmitMessage('');
    try {
      const payload = {
        planId: selectedPlan?.id || null,
        planName: selectedPlan?.name || null,
        phone: phone,
      };
      console.log('[Pricing] Submitting lead with payload:', payload);
      const res = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      console.log('[Pricing] Lead submission response status:', res.status);
        if (!res.ok) {
          let errText = 'Failed to submit request';
          try {
            const errBody = await res.json();
            console.error('[Pricing] Lead submission error body:', errBody);
            if (errBody && errBody.error) errText = errBody.error;
          } catch (e) { 
            console.error('[Pricing] Could not parse error response:', e);
          }
          throw new Error(errText);
        }
      console.log('[Pricing] Lead submitted successfully!');
      setSubmitMessage('Thank you! We will contact you shortly.');
      setPhone('');
      setTimeout(() => { setShowModal(false); setSubmitMessage(''); }, 2000);
    } catch (err) {
      console.error('[Pricing] Lead submission error:', err);
      setSubmitMessage('Submission failed. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    console.log('[Pricing] Loading state...');
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-xl text-white font-semibold">Loading pricing plans...</div>
      </div>
    );
  }

  if (error) {
    console.error('[Pricing] Error state:', error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-xl text-red-400 font-semibold">Error: {error}</div>
      </div>
    );
  }

  console.log('[Pricing] Rendering with plans:', plans.length);

  return (
    <div className="bg-gray-50">
      <SEO 
        title="Pricing Plans - Affordable Hotel & Restaurant Management"
        description="Choose the perfect plan for your business. Flexible pricing for hotels, restaurants, cafes, and enterprises. Get started with Trivana's management software at competitive rates with no hidden fees."
        keywords="hotel software pricing, restaurant POS pricing, management software cost, affordable hospitality software, hotel management plans, restaurant management pricing"
        url="/pricing"
      />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center animate-fade-in-up">
            <div className="inline-block px-6 py-2 bg-gradient-to-r from-[#4299f5]/20 to-[#2d7dd1]/20 text-[#4299f5] rounded-full font-semibold text-sm mb-6 border border-[#4299f5]/30">
              Transparent Pricing - No Hidden Fees
            </div>
            <h1 className="text-4xl font-extrabold text-white sm:text-6xl mb-6" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Choose Your Perfect <span className="text-[#4299f5]">Plan</span>
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-8">
              Start transforming your hospitality business today with affordable, flexible pricing
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm animate-fade-in-up delay-200">
              <span className="flex items-center gap-2 text-green-400 font-semibold bg-green-500/10 px-4 py-2 rounded-full border border-green-500/30">
                <CheckCircleIcon className="w-5 h-5" />
                7-Day Free Trial
              </span>
              <span className="flex items-center gap-2 text-green-400 font-semibold bg-green-500/10 px-4 py-2 rounded-full border border-green-500/30">
                <CheckCircleIcon className="w-5 h-5" />
                Cancel Anytime
              </span>
              <span className="flex items-center gap-2 text-green-400 font-semibold bg-green-500/10 px-4 py-2 rounded-full border border-green-500/30">
                <CheckCircleIcon className="w-5 h-5" />
                24/7 Support
              </span>
            </div>
          </div>
        </div>
        {/* Decorative elements  */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Choose by <span className="text-[#4299f5]">Bundle</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
            We have grouped individual plans into practical bundles so you can compare faster and pick what matches your business stage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up delay-300">
          {bundledPlans.map((bundle, index) => (
            <div
              key={bundle.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#4299f5] transform hover:-translate-y-1 overflow-hidden"
            >
              {/* Plan Header */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-5 border-b border-gray-200">
                {index === 0 && (
                  <span className="inline-block mb-3 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold border border-orange-200">
                    Top Trending
                  </span>
                )}
                <h3 className="text-xl font-bold mb-2 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>{bundle.name}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{bundle.description}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] bg-clip-text text-transparent">₹{formatCurrency(bundle.percentagePrice)}</span>
                  <span className="text-gray-500 text-sm font-medium">for 1 year</span>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {bundle.percentage}% of yearly total ₹{formatCurrency(bundle.yearlyTotal)}
                </p>
                <p className="mt-1 text-xs text-gray-500">Includes {bundle.plans.length} plan{bundle.plans.length > 1 ? 's' : ''}</p>
              </div>

              <div className="p-4 border-b border-gray-100">
                <h4 className="font-bold text-gray-900 mb-3 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>Plans in this bundle</h4>
                <ul className="space-y-2">
                  {bundle.plans.slice(0, 4).map((plan) => (
                    <li key={plan.id} className="flex items-center justify-between text-xs text-gray-700 bg-blue-50/70 px-3 py-2 rounded-lg border border-blue-100">
                      <span className="font-semibold truncate pr-3">{plan.name}</span>
                      <span className="font-bold text-[#4299f5]">₹{formatCurrency(parsePrice(plan.basePrice) * MONTHS_IN_YEAR)} / year</span>
                    </li>
                  ))}
                  {bundle.plans.length > 4 && (
                    <li className="text-xs text-gray-500">+{bundle.plans.length - 4} more plan options</li>
                  )}
                </ul>
              </div>

              {expandedBundleId === bundle.id && (
                <div className="p-4 bg-white">
                  <h4 className="font-bold text-gray-900 mb-3 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>Bundle details</h4>
                  <div className="space-y-3">
                    {bundle.plans.map((plan) => (
                      <div key={`details-${plan.id}`} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{plan.name}</p>
                            {plan.description && (
                              <p className="text-xs text-gray-600 mt-1 leading-relaxed">{plan.description}</p>
                            )}
                          </div>
                          <span className="text-sm font-bold text-[#4299f5] whitespace-nowrap">₹{formatCurrency(parsePrice(plan.basePrice) * MONTHS_IN_YEAR)} / year</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white">
                <button
                  onClick={() => setExpandedBundleId(expandedBundleId === bundle.id ? null : bundle.id)}
                  className="w-full mb-3 bg-white text-[#2d7dd1] border border-[#4299f5]/40 font-bold py-2.5 px-5 rounded-lg hover:bg-blue-50 transition-all duration-300 text-sm"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  {expandedBundleId === bundle.id ? 'Hide Bundle Details' : 'View Bundle Details'}
                </button>
                <button
                  onClick={() => { 
                    console.log('[Pricing] Get Started clicked for bundle:', bundle); 
                    setSelectedPlan({ id: null, name: bundle.name }); 
                    setShowModal(true); 
                  }}
                  className="w-full bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] text-white font-bold py-3 px-5 rounded-lg hover:from-[#2d7dd1] hover:to-[#4299f5] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        {bundledPlans.length === 0 && !loading && !error && (
          <div className="text-center py-16">
            <p className="text-xl text-gray-300">No pricing plans available at the moment.</p>
          </div>
        )}

        {/* Why Choose Section */}
        <div className="mt-24 bg-white rounded-3xl shadow-2xl p-10 sm:p-14">
          <h2 className="text-4xl font-bold text-center mb-14 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Why Businesses Choose <span className="text-[#4299f5]">Trivana</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Setup in Minutes", desc: "Get started in under 10 minutes" },
              { title: "Affordable Pricing", desc: "Plans for every budget" },
              { title: "Grow at Your Pace", desc: "Upgrade or downgrade anytime" },
              { title: "Secure & Reliable", desc: "99.9% uptime guarantee" }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-l-4 border-[#4299f5] hover:shadow-lg transition-all duration-300">
                <h3 className="text-xl font-bold mb-3 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-24">
          <h2 className="text-4xl font-bold text-center mb-14 text-white" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Frequently Asked <span className="text-[#4299f5]">Questions</span>
          </h2>
          <div className="max-w-4xl mx-auto space-y-5">
            {[
              { q: "Can I try before I buy?", a: "Yes! We offer a 7-day free trial on all plans. No credit card required." },
              { q: "Can I change my plan later?", a: "Absolutely! You can upgrade or downgrade your plan at any time." },
              { q: "What payment methods do you accept?", a: "We accept UPI, Credit/Debit cards, Net Banking, and other digital payment methods." },
              { q: "Do you offer refunds?", a: "Yes, we offer a 30-day money-back guarantee if you're not satisfied." },
              { q: "Is training included?", a: "Yes! We provide free onboarding training and 24/7 customer support." }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-xl p-7 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-[#4299f5]/50">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  <CheckCircleIcon className="w-6 h-6 text-[#4299f5] flex-shrink-0 mt-0.5" />
                  {faq.q}
                </h3>
                <p className="text-gray-700 pl-9 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-24 text-center bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] rounded-3xl p-14 shadow-2xl">
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-5 text-white" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/95 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join hundreds of hospitality businesses already using Trivana
          </p>
          <a
            href="/contact"
            className="inline-block px-6 py-3 sm:px-8 sm:py-4 lg:px-12 lg:py-5 bg-white text-[#4299f5] text-base sm:text-lg font-bold rounded-full shadow-2xl hover:shadow-white/30 transition-all duration-300 transform hover:scale-105"
            style={{fontFamily: 'Montserrat, sans-serif'}}
          >
            Contact Sales for Custom Plan
          </a>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-11/12 max-w-md p-8 shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Get Started - <span className="text-[#4299f5]">{selectedPlan?.name}</span>
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <XMarkIcon className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <form onSubmit={submitLead}>
              <label className="block text-sm font-bold text-gray-800 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. +919876543210"
                className="w-full px-5 py-3.5 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-[#4299f5] focus:border-[#4299f5] mb-5 bg-white text-gray-900 transition-all"
                required
              />

              {submitMessage && (
                <div className="mb-4 p-3 text-sm text-center bg-blue-50 text-gray-800 rounded-lg border border-blue-200">{submitMessage}</div>
              )}

              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-6 py-3 rounded-xl bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting} 
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] text-white font-semibold hover:from-[#2d7dd1] hover:to-[#4299f5] transition-all disabled:opacity-50"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
